import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import PhotoUploadGrid from './PhotoUploadGrid';
import ReferenceDropdown from './ReferenceDropdown';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editData?: any;
}

interface DeviceFormData {
  item_name: string;
  company: string;
  hsn_code: string;
  category: string;
  priority: number | '';
  reference_id: string;
  status: string;
  purchase_date: string;
  purchase_cost: number | '';
  monthly_rental_rate: number | '';
  daily_rental_rate: number | '';
  selling_price: number | '';
  gst_applicable: string;
  photos: (File | null)[];
}

const DeviceRegistrationModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, editData }) => {
  const defaultForm: DeviceFormData = {
    item_name: '',
    company: '',
    hsn_code: '',
    category: '',
    priority: '',
    reference_id: '',
    status: 'Functional',
    purchase_date: '',
    purchase_cost: '',
    monthly_rental_rate: '',
    daily_rental_rate: '',
    selling_price: '',
    // Empty = no GST recorded, shown as N/A. The old 'Non-GST' sentinel is
    // gone: it was saved as NULL anyway, so it was a second way to say empty.
    gst_applicable: '',
    photos: [null, null, null, null, null],
  };

  const [formData, setFormData] = useState<DeviceFormData>(defaultForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Catalog State
  const [catalogOptions, setCatalogOptions] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);

  // Fetch Catalog Options when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCatalog();
      // If we are editing, pre-fill the form!
      if (editData) {
        setFormData({
          item_name: editData.item_name || '',
          company: editData.company || '',
          hsn_code: editData.hsn_code || '',
          category: editData.category || '',
          priority: editData.priority ?? '',
          reference_id: editData.reference_id || '',
          status: editData.status || 'Functional',
          purchase_date: editData.purchase_date || '',
          purchase_cost: editData.purchase_cost || '',
          monthly_rental_rate: editData.monthly_rental_rate || '',
          daily_rental_rate: editData.daily_rental_rate || '',
          selling_price: editData.selling_price || '',
          gst_applicable: editData.gst_applicable === 'Non-GST' ? '' : (editData.gst_applicable || ''),
          photos: [null, null, null, null, null],
        });
      } else {
        setFormData(defaultForm); // Clean slate for new devices
      }
    } else {
      setFormData(defaultForm);
      setError('');
    }
  }, [isOpen, editData]);

  const fetchCatalog = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      const token = localStorage.getItem('accessToken');
      const res = await axios.get(`${API_URL}/api/device-catalog/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCatalogOptions(res.data.map((item: any) => item.item_name));
    } catch (err) {
      console.error("Failed to load device catalog", err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.item_name.trim()) {
      setError("Item Name is mandatory.");
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      const token = localStorage.getItem('accessToken');
      
      // 1. Prepare JSON Payload
      const jsonPayload = {
        item_name: formData.item_name,
        company: formData.company.trim() || null,
        hsn_code: formData.hsn_code.trim() || null,
        category: formData.category.trim() || null,
        priority: formData.priority === '' ? null : Number(formData.priority),
        reference_id: formData.reference_id.trim() || null,
        status: formData.status,
        purchase_date: formData.purchase_date || null,
        purchase_cost: formData.purchase_cost === '' ? null : Number(formData.purchase_cost),
        monthly_rental_rate: formData.monthly_rental_rate === '' ? null : Number(formData.monthly_rental_rate),
        daily_rental_rate: formData.daily_rental_rate === '' ? null : Number(formData.daily_rental_rate),
        selling_price: formData.selling_price === '' ? null : Number(formData.selling_price),
        gst_applicable: formData.gst_applicable.trim() === '' ? null : formData.gst_applicable.trim()
      };

      // 2. Build Multipart Form Data
      const payload = new FormData();
      payload.append('device_data', JSON.stringify(jsonPayload));

      formData.photos.forEach((photo, i) => { if (photo) payload.append(`photo_${i + 1}`, photo); });

      // --- THIS IS THE UPDATED PART ---
      // 3. Send Request (PATCH if editing, POST if new)
      if (editData && editData.id) {
        await axios.patch(`${API_URL}/api/medical-devices/${editData.id}`, payload, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        toast.success("Device Updated Successfully!");
      } else {
        await axios.post(`${API_URL}/api/medical-devices/`, payload, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        toast.success("Device Registered Successfully!");
      }
      // --------------------------------

      onSuccess(); 
      onClose(); 
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save device');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dropdown Logic: Show 5 max, unless 2+ chars typed
  const filteredOptions = catalogOptions.filter(opt => 
    opt.toLowerCase().includes(formData.item_name.toLowerCase())
  );
  const visibleOptions = formData.item_name.length < 2 
    ? filteredOptions.slice(0, 5) 
    : filteredOptions;

  const modalStyles = {
    overlay: { position: 'fixed' as const, inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' },
    box: { backgroundColor: 'white', borderRadius: '12px', width: '95%', maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' as const, padding: '2rem', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
    sectionTitle: { fontSize: '1rem', fontWeight: 700, color: '#0f172a', margin: '1.5rem 0 1rem 0', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0' },
    input: { width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '0.85rem', boxSizing: 'border-box' as const, height: '40px' },
    label: { display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.5px', marginBottom: '0.3rem' },
    uploadBox: { border: '2px dashed #cbd5e1', borderRadius: '8px', padding: '1rem', textAlign: 'center' as const, cursor: 'pointer', backgroundColor: '#f8fafc', transition: 'all 0.2s' },
    row: { display: 'flex', gap: '1rem', marginBottom: '1rem' },
    col: { flex: 1, position: 'relative' as const }
  };

  if (!isOpen) return null;

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.box} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, color: '#1e293b', fontSize: '1.5rem', fontWeight: 800 }}>Register Medical Device</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={24}/></button>
        </div>

        {error && <div style={{ padding: '0.75rem', backgroundColor: '#fef2f2', color: '#ef4444', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.875rem', border: '1px solid #fecaca' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          
          {/* --- SECTION 1: ASSET DETAILS --- */}
          <div style={modalStyles.sectionTitle}>1. Asset Details</div>
          
          {/* Photo Upload — up to 5 photos */}
          <div style={{ marginBottom: '1rem' }}>
            <PhotoUploadGrid
              files={formData.photos}
              onChange={photos => setFormData(p => ({ ...p, photos }))}
            />
          </div>

          <div style={modalStyles.row}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Item Name (Creatable Select) */}
              <div style={modalStyles.col}>
                <label style={modalStyles.label}>Item Name <span style={{color: '#ef4444'}}>*</span></label>
                <input 
                  type="text" required style={modalStyles.input} placeholder="Start typing to add or select..."
                  value={formData.item_name} 
                  onChange={e => setFormData({...formData, item_name: e.target.value})}
                  onFocus={() => setShowDropdown(true)}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                />
                {/* Custom Autocomplete Dropdown */}
                {showDropdown && visibleOptions.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', zIndex: 10, maxHeight: '150px', overflowY: 'auto', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                    {visibleOptions.map((opt, idx) => (
                      <div 
                        key={idx} 
                        style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f1f5f9' }}
                        onMouseDown={() => { setFormData({...formData, item_name: opt}); setShowDropdown(false); }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                      >
                        {opt}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Company + HSN Code */}
              <div style={modalStyles.row}>
                <div style={modalStyles.col}>
                  <label style={modalStyles.label}>Company <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                  <input
                    type="text"
                    style={modalStyles.input}
                    placeholder="e.g. Philips"
                    value={formData.company}
                    onChange={e => setFormData({ ...formData, company: e.target.value })}
                  />
                </div>
                <div style={modalStyles.col}>
                  <label style={modalStyles.label}>HSN Code <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                  <input
                    type="text"
                    style={modalStyles.input}
                    placeholder="e.g. 9018"
                    value={formData.hsn_code}
                    onChange={e => setFormData({ ...formData, hsn_code: e.target.value })}
                  />
                </div>
              </div>

              {/* Category + Priority */}
              <div style={modalStyles.row}>
                <div style={modalStyles.col}>
                  <label style={modalStyles.label}>Category <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                  <input
                    type="text"
                    style={modalStyles.input}
                    placeholder="e.g. Respiratory"
                    value={formData.category}
                    onChange={e => setFormData({ ...formData, category: e.target.value })}
                  />
                </div>
                <div style={modalStyles.col}>
                  <label style={modalStyles.label}>Priority <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    style={modalStyles.input}
                    placeholder="e.g. 1"
                    value={formData.priority}
                    onChange={e => setFormData({ ...formData, priority: e.target.value ? Number(e.target.value) : '' })}
                  />
                </div>
              </div>

              {/* Reference ID */}
              <div style={modalStyles.col}>
                <label style={modalStyles.label}>Reference ID <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                <input
                  type="text"
                  style={modalStyles.input}
                  placeholder="e.g. EQ-2024-001"
                  value={formData.reference_id}
                  onChange={e => setFormData({ ...formData, reference_id: e.target.value })}
                />
              </div>

              {/* Status */}
              <div style={modalStyles.col}>
                <label style={modalStyles.label}>Asset Status</label>
                <select style={modalStyles.input} value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>
                  <option value="Functional">Functional</option>
                  <option value="Not Working">Not Working</option>
                  <option value="In Repair">In Repair</option>
                  <option value="Rented Out">Rented Out</option>
                </select>
              </div>
            </div>
          </div>

          {/* --- SECTION 2: FINANCIAL DETAILS --- */}
          <div style={modalStyles.sectionTitle}>2. Financial Details</div>
          
          <div style={modalStyles.row}>
            <div style={modalStyles.col}>
              <label style={modalStyles.label}>Date of Purchase</label>
              <input type="date" style={modalStyles.input} value={formData.purchase_date} onChange={e => setFormData({...formData, purchase_date: e.target.value})} />
            </div>
            <div style={modalStyles.col}>
              <label style={modalStyles.label}>Purchase Cost</label>
              <input type="number" min="0" style={modalStyles.input} placeholder="₹ 0.00" value={formData.purchase_cost} onChange={e => setFormData({...formData, purchase_cost: e.target.value ? Number(e.target.value) : ''})} />
            </div>
          </div>

          <div style={modalStyles.row}>
            <div style={modalStyles.col}>
              <label style={modalStyles.label}>Monthly Rental</label>
              <input type="number" min="0" style={modalStyles.input} placeholder="₹ 0.00" value={formData.monthly_rental_rate} onChange={e => setFormData({...formData, monthly_rental_rate: e.target.value ? Number(e.target.value) : ''})} />
            </div>
            <div style={modalStyles.col}>
              <label style={modalStyles.label}>Daily Rental</label>
              <input type="number" min="0" style={modalStyles.input} placeholder="₹ 0.00" value={formData.daily_rental_rate} onChange={e => setFormData({...formData, daily_rental_rate: e.target.value ? Number(e.target.value) : ''})} />
            </div>
          </div>

          <div style={modalStyles.row}>
            <div style={modalStyles.col}>
              <label style={modalStyles.label}>Selling Price (₹)</label>
              <input type="number" min="0" style={modalStyles.input} placeholder="₹ 0.00 (for outright sale)" value={formData.selling_price} onChange={e => setFormData({...formData, selling_price: e.target.value ? Number(e.target.value) : ''})} />
            </div>
            <div style={modalStyles.col}>
              {/* Add-to-list rather than a fixed set, so a rate the business
                  starts charging can be added here instead of needing a code
                  change. Left blank the device has no GST — the billing sheet
                  reads that as none, and it shows as N/A on the device list. */}
              <ReferenceDropdown
                label="GST Applicable"
                category="device_gst"
                value={formData.gst_applicable}
                onChange={(val) => setFormData({ ...formData, gst_applicable: val })}
              />
              <span style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: '2px' }}>
                Leave blank for N/A. Use the "5%" form so billing can read the rate.
              </span>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem', borderTop: '1px solid #e2e8f0', paddingTop: '1.5rem' }}>
            <button type="button" onClick={onClose} style={{ padding: '0.6rem 1.5rem', borderRadius: '6px', border: 'none', backgroundColor: '#e2e8f0', color: '#475569', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
            <button type="submit" disabled={isSubmitting} style={{ padding: '0.6rem 1.5rem', borderRadius: '6px', border: 'none', backgroundColor: '#2563eb', color: 'white', cursor: 'pointer', fontWeight: 600 }}>
              {isSubmitting ? 'Saving Device...' : 'Register Asset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeviceRegistrationModal;